import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import {
  useCreateBand,
  useCreatePackage,
  useDeleteBand,
  useUpdateBand,
} from '@/api/queries';
import type { Band, EventCard, Package } from '@/api/types';
import { Button } from '@/components/kit/Button';
import {
  BandDot,
  BAND_PALETTE,
  ChoiceField,
  ColorFan,
  Option,
  RowAction,
} from '@/components/kit/Controls';
import { Form, TextField } from '@/components/kit/Field';
import { ActionBar } from '@/components/kit/ActionBar';
import { Modal } from '@/components/kit/Modal';
import { EmptyState, Note } from '@/components/kit/Note';
import { SectionHeader } from '@/components/kit/ObjectTabs';
import { Panel } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { CardLabel, MobileSub, Table } from '@/components/kit/Table';
import { useToast } from '@/components/kit/Toast';
import { classes, date, money, nominations as nomPlural, num } from '@/lib/format';
import { PACKAGE_KIND, PACKAGE_STATE } from '../state';

/**
 * Вкладка «Пакеты и цены» плюс браслеты мероприятия.
 *
 * Пакеты не группируются: один список. Группировка повторяла бы то,
 * что видно в составе, и мешала искать по названию (БЛ §12).
 *
 * Состав здесь не показан строками — он в карточке пакета: сжатая
 * строка состава всё равно требует разгадывания.
 */
export function PackagesTab({ event }: { event: EventCard }) {
  const navigate = useNavigate();
  const [newPackage, setNewPackage] = useState(false);
  const [editingBand, setEditingBand] = useState<Band | 'new' | null>(null);
  // Пакеты и браслеты — два самостоятельных списка, значит подразделы (кит 2.11)
  const [subsection, setSubsection] = useState<'packages' | 'bands'>('packages');

  const brief = (p: Package) => {
    const parts: string[] = [];
    if (p.nominations.length) parts.push(`участие · ${nomPlural(p.nominations.length)}`);

    const services = p.serviceIds
      .map((id) => event.services.find((s) => s.id === id))
      .filter(Boolean);

    const mk = services.filter((s) => s!.kind === 'masterclass').length;
    if (mk) parts.push(classes(mk));

    const tickets = services.filter((s) => s!.kind === 'ticket');
    if (tickets.length) {
      const names = tickets
        .map((t) => event.blocks.find((b) => b.id === t!.blockId)?.name ?? 'без блока')
        .join(' + ');
      parts.push(tickets.length > 1 ? `билеты ${names}` : `билет ${names}`);
    }

    const showcase = services.find((s) => s!.kind === 'showcase');
    if (showcase) parts.push(showcase.selection === 'video' ? 'шоукейс по отбору' : 'шоукейс');

    return parts.join(' · ');
  };

  const action =
    subsection === 'packages' ? (
      <Button small tone="quiet" onClick={() => setNewPackage(true)}>
        ＋ Пакет
      </Button>
    ) : (
      <Button small tone="quiet" onClick={() => setEditingBand('new')}>
        ＋ Браслет
      </Button>
    );

  return (
    <>
      <Panel>
        <SectionHeader
          segments={{
            value: subsection,
            onChange: setSubsection,
            items: [
              { value: 'packages', label: 'Пакеты' },
              { value: 'bands', label: 'Браслеты' },
            ],
          }}
          actions={action}
        />

        {subsection === 'bands' ? (
          <BandsSection event={event} onEdit={setEditingBand} />
        ) : !event.packages.length ? (
          <EmptyState>
            Пакетов пока нет. Пакет — то, что покупает участник: участие плюс классы и билеты
          </EmptyState>
        ) : (
          <Table
            valueWidth={150}
            head={
              <tr>
                <th className="srt">
                  Пакет <span className="ar">▲</span>
                </th>
                <th>Статус</th>
                <th>Тип</th>
                <th className="r">Цена</th>
                <th className="r">Повышение</th>
                <th className="r">Услуг</th>
                <th className="r">Номинаций</th>
                <th />
              </tr>
            }
          >
            {event.packages.map((p) => {
              const state = PACKAGE_STATE[p.state];
              return (
                <tr key={p.id} className="clk" onClick={() => navigate(`/events/${event.id}/packages/${p.id}`)}>
                  <td data-col="primary">
                    <span className="nm">{p.name}</span>
                    {!p.visibleToClient ? <Pill tone="mut">скрытый</Pill> : null}
                    <MobileSub>{brief(p) || 'состав пуст'}</MobileSub>
                  </td>
                  <td data-col="secondary">
                    <Pill tone={state.tone}>{state.label}</Pill>
                  </td>
                  <td className="muted" data-col="hide">
                    {PACKAGE_KIND[p.kind]}
                  </td>
                  <td className="r" data-col="sec2">
                    <CardLabel>цена</CardLabel>
                    <span className="num">
                      {money(p.priceFirst)}
                      {p.priceSecond ? ` → ${money(p.priceSecond)}` : ''}
                    </span>
                  </td>
                  <td className="r num muted" data-col="hide">
                    {p.priceSecond ? (
                      <>
                        {date(p.raiseOn ?? event.priceRaiseDefault)}
                        {p.raiseAfterQty ? ` · ${num(p.raiseAfterQty)} шт` : ''}
                      </>
                    ) : (
                      <span className="dim">не повышается</span>
                    )}
                  </td>
                  <td className="r num muted" data-col="hide">
                    {p.serviceIds.length || <span className="na">—</span>}
                  </td>
                  <td className="r num muted" data-col="hide">
                    {p.nominations.length || <span className="na">—</span>}
                  </td>
                  <td className="r" data-col="act" />
                </tr>
              );
            })}
          </Table>
        )}

        {subsection === 'packages' ? (
          <>
            <Note>
              Что именно входит в пакет — в его карточке: там состав виден строками, а не сжатой строкой,
              которую всё равно приходится разгадывать
            </Note>
            <Note>
              <b>Тип различает только одно:</b> основной покупают как своё участие, дополнительный —
              вдобавок к уже оплаченному. Повторное участие и руководителя в составе нельзя купить
              вместо основного пакета
            </Note>
          </>
        ) : null}
      </Panel>

      <ActionBar>{action}</ActionBar>

      {newPackage ? (
        <NewPackageModal event={event} onClose={() => setNewPackage(false)} />
      ) : null}

      {editingBand ? (
        <BandModal
          event={event}
          band={editingBand === 'new' ? null : editingBand}
          onClose={() => setEditingBand(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Браслеты. Список принадлежит мероприятию, а не общему справочнику:
 * их печатают под конкретный город (БЛ §3.6).
 */
function BandsSection({
  event,
  onEdit,
}: {
  event: EventCard;
  onEdit: (band: Band | 'new') => void;
}) {
  const remove = useDeleteBand(event.id);
  const toast = useToast();

  return (
    <>
      {!event.bands.length ? (
        <EmptyState>Браслетов нет. Браслет — цветная метка на площадке, производная от пакета</EmptyState>
      ) : (
        <Table
          head={
            <tr>
              <th>Цвет</th>
              <th>Назначение</th>
              <th className="r">В пакетах</th>
              <th />
            </tr>
          }
        >
          {event.bands.map((b) => (
            <tr key={b.id} className="clk" onClick={() => onEdit(b)}>
              <td data-col="primary">
                <BandDot color={b.color} />
                <span className="nm">{b.name}</span>
                <MobileSub>{b.purpose ?? '—'}</MobileSub>
              </td>
              <td className="muted" data-col="hide">
                {b.purpose ?? '—'}
              </td>
              <td className="r" data-col="secondary">
                <CardLabel>в пакетах</CardLabel>
                <span className="num">{b.packageCount || <span className="na">—</span>}</span>
              </td>
              <td className="r" data-col="act">
                {/* Удалить нельзя, пока браслет стоит в пакетах (БЛ §5.6).
                    Сервер откажет в любом случае — здесь просто не показываем
                    действие, которое заведомо не сработает. */}
                {b.packageCount ? null : (
                  <RowAction
                    title="Удалить браслет"
                    onClick={() =>
                      remove.mutate(b.id, {
                        onSuccess: () => toast.show(`Браслет «${b.name}» удалён`),
                        onError: (e) =>
                          toast.showError(e instanceof ApiError ? e.message : 'Не удалилось'),
                      })
                    }
                  />
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}

function BandModal({
  event,
  band,
  onClose,
}: {
  event: EventCard;
  band: Band | null;
  onClose: () => void;
}) {
  const create = useCreateBand(event.id);
  const update = useUpdateBand(event.id);
  const toast = useToast();

  const [name, setName] = useState(band?.name ?? '');
  const [color, setColor] = useState(band?.color ?? BAND_PALETTE[0]);
  const [purpose, setPurpose] = useState(band?.purpose ?? '');

  const submit = () => {
    const body = { name, color, purpose: purpose || null };
    const done = {
      onSuccess: () => {
        toast.show(band ? 'Браслет изменён' : `Браслет «${name}» добавлен`);
        onClose();
      },
      onError: (e: Error) =>
        toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось'),
    };

    if (band) update.mutate({ id: band.id, ...body }, done);
    else create.mutate(body, done);
  };

  return (
    <Modal
      title={band ? `Браслет «${band.name}»` : 'Новый браслет'}
      onClose={onClose}
      confirm={{ label: 'Сохранить', onClick: submit, disabled: !name.trim() }}
    >
      <Form>
        <TextField label="Название цвета" value={name} onValue={setName} placeholder="оранжевый" />
        <div className="field">
          <span className="lbl">Цвет</span>
          <ColorFan value={color} onChange={setColor} />
        </div>
        <TextField
          label="Назначение"
          value={purpose}
          onValue={setPurpose}
          placeholder="BEGINNER PASS"
        />
      </Form>
      <Note>
        Браслеты печатают под конкретный город, поэтому список принадлежит мероприятию,
        а не общему справочнику
      </Note>
    </Modal>
  );
}

/**
 * Новый пакет. Создаётся черновиком; состав можно скопировать
 * из другого пакета — признак «по умолчанию» при этом сбрасывается,
 * иначе у номинации станет два пакета по умолчанию.
 *
 * Поповера в окне нет: выбор источника — список опций.
 */
function NewPackageModal({ event, onClose }: { event: EventCard; onClose: () => void }) {
  const create = useCreatePackage(event.id);
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'main' | 'extra'>('main');
  const [priceFirst, setPriceFirst] = useState('');
  const [copyFrom, setCopyFrom] = useState<string | null>(null);

  const price = Number(priceFirst.replace(/[^\d]/g, ''));

  const submit = () =>
    create.mutate(
      {
        name,
        kind,
        priceFirst: price,
        copyCompositionFrom: copyFrom ?? undefined,
      },
      {
        onSuccess: (created) => {
          toast.show(`Пакет «${name}» создан черновиком`);
          onClose();
          navigate(`/events/${event.id}/packages/${created.id}`);
        },
        onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не создалось'),
      },
    );

  return (
    <Modal
      title="Новый пакет"
      onClose={onClose}
      confirm={{
        label: 'Создать',
        onClick: submit,
        disabled: !name.trim() || !price || create.isPending,
      }}
    >
      <Form>
        <TextField label="Название" value={name} onValue={setName} placeholder="BEGINNER PASS" />
        <ChoiceField
          label="Тип"
          value={kind}
          onChange={setKind}
          items={[
            { value: 'main', label: 'Основной' },
            { value: 'extra', label: 'Дополнительный' },
          ]}
        />
        <TextField
          label="Первая цена"
          value={priceFirst}
          onValue={setPriceFirst}
          placeholder="3600"
        />
      </Form>

      {event.packages.length ? (
        <div className="field">
          <span className="lbl">Скопировать состав</span>
          <Option
            radio
            checked={copyFrom === null}
            onToggle={() => setCopyFrom(null)}
            title="Собрать состав с нуля"
          />
          {event.packages.map((p) => (
            <Option
              key={p.id}
              radio
              checked={copyFrom === p.id}
              onToggle={() => setCopyFrom(p.id)}
              title={p.name}
              description={`${p.serviceIds.length} услуг · ${p.nominations.length} номинаций`}
            />
          ))}
          <Note>
            При копировании состава признак «по умолчанию» сбрасывается — иначе у номинации
            станет два пакета по умолчанию
          </Note>
        </div>
      ) : null}
    </Modal>
  );
}
